<!-- MOLECULE: ExplorerToolbar.vue -->
<!-- Toolbar with filter and action controls for the validation explorer. -->
<script setup lang="ts">
//
// Imports
//

// Vuetify
import { useDisplay } from "vuetify/lib/composables/display.mjs";

//
// Composables
//

// Localization
const { t } = useI18n();

// Breakpoints
const { smAndDown } = useDisplay();

//
// Props
//
const props = withDefaults(
  defineProps<{
    signalRConnected: boolean;
    signalRReconnecting: boolean;
    isRefreshing: boolean;
    searchValue: string;
    hasActiveFilters?: boolean;
    filteredSamplesCount?: number;
    loading?: boolean;
    filtersLoading?: boolean;
    onRetry: () => void;
    onFilter: () => void;
    onSearchUpdate: (value: string) => void;
  }>(),
  {
    hasActiveFilters: false,
    filteredSamplesCount: 0,
    loading: false,
    filtersLoading: false,
  },
);

//
// Methods
//
const handleSearchUpdate = (value: unknown) => {
  const stringValue = value == null ? "" : String(value);
  props.onSearchUpdate(stringValue);
};
</script>

<template>
  <div v-no-autocomplete class="w-100 d-flex flex-wrap align-center ga-1" :style="smAndDown ? '' : 'min-width: 330px;'">
    <!-- SignalR Status -->
    <v-tooltip
      :text="
        signalRReconnecting
          ? t('pages.validation.signalr.reconnecting')
          : signalRConnected
            ? t('pages.validation.signalr.connected')
            : t('pages.validation.signalr.disconnected')
      "
      location="bottom"
      :open-delay="700"
      :close-delay="300"
    >
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          icon
          variant="text"
          size="small"
          :color="signalRReconnecting ? 'warning' : signalRConnected ? 'success' : 'error'"
          class="cursor-help"
          :loading="signalRReconnecting"
        >
          <v-icon :icon="signalRConnected ? 'mdi-access-point' : 'mdi-access-point-off'" size="22"></v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <!-- Refresh Button -->
    <v-tooltip
      :text="t('pages.validation.toolbar.refreshTooltip')"
      location="bottom"
      :open-delay="700"
      :close-delay="300"
    >
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          icon
          variant="text"
          size="small"
          color="white"
          :loading="loading || isRefreshing"
          @click="onRetry"
        >
          <v-icon icon="mdi-refresh" size="22"></v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <!-- Filter Button -->
    <v-tooltip :text="t('pages.validation.toolbar.filterTooltip')" location="bottom" :open-delay="700">
      <template #activator="{ props: tooltipProps }">
        <!-- With Active Filters Indicator -->
        <template v-if="props.hasActiveFilters">
          <v-badge
            location="top right"
            color="error"
            :height="18"
            :content="loading || isRefreshing ? '...' : filteredSamplesCount"
            class="filter-badge-pill"
          >
            <v-btn
              v-bind="tooltipProps"
              icon
              variant="text"
              color="white"
              size="small"
              :loading="filtersLoading"
              @click="onFilter"
            >
              <v-icon icon="mdi-filter-outline" size="22"></v-icon>
            </v-btn>
          </v-badge>
        </template>

        <!--Without Active Filters Indicator -->
        <template v-else>
          <v-btn
            v-bind="tooltipProps"
            icon
            variant="text"
            color="white"
            size="small"
            :loading="filtersLoading"
            @click="onFilter"
          >
            <v-icon icon="mdi-filter-outline" size="22"></v-icon>
          </v-btn>
        </template>
      </template>
    </v-tooltip>

    <!-- Search Field -->
    <v-text-field
      class="toolbar-search-field mx-2"
      type="text"
      :model-value="String(searchValue || '')"
      :label="t('pages.validation.toolbar.searchPlaceholder')"
      variant="outlined"
      prepend-inner-icon="mdi-magnify"
      clear-icon="mdi-close-circle-outline"
      rounded="lg"
      clearable
      hide-details
      density="compact"
      @update:model-value="handleSearchUpdate"
    />
  </div>
</template>

<style scoped>
/* Scope styles to this component's search field only */
.toolbar-search-field.v-input--density-compact {
  --v-input-control-height: 32px !important;
  --v-input-padding-top: 0px !important;
}

.toolbar-search-field.v-input--density-compact :deep(.v-field--variant-outlined),
.toolbar-search-field.v-input--density-compact :deep(.v-field--single-line),
.toolbar-search-field.v-input--density-compact :deep(.v-field--no-label) {
  --v-field-padding-bottom: 0px !important;
}

.toolbar-search-field :deep(.v-field),
.toolbar-search-field :deep(.v-label) {
  font-size: small;
}

.toolbar-search-field :deep(.v-label),
.toolbar-search-field :deep(.v-field__prepend-inner > .v-icon),
.toolbar-search-field :deep(.v-field__append-inner > .v-icon),
.toolbar-search-field :deep(.v-field__clearable > .v-icon) {
  opacity: 1 !important;
}

/* Pill-shaped filter badge that scales with content */
.filter-badge-pill :deep(.v-badge__badge) {
  font-size: 0.65rem !important;
  padding: 0 4px !important;
  min-width: 18px !important;
}
</style>
